import React, { useEffect, useState } from 'react';
import { KeyboardShift } from '../ui/KeyboardShift';
import {
  View, Pressable, ActivityIndicator, ScrollView, Platform, StyleSheet, useWindowDimensions,
} from 'react-native';
import { Text, TextInput } from '../ui/Text';
import { useAuth } from '../auth';
import { HS_LABEL } from '../config';
import { makeStyles, useTheme, radius } from '../theme';

/* Sign in. The default is an emailed code: type your email, we send a six-digit code, type it, you are in.
   No password to set, remember or reset. A password form stays one tap away for admins, agents and anyone
   whose account has no email on file yet. Same screen on iOS, Android and web; desktop web gets a card. */

type Mode = 'email' | 'code' | 'password';
const RESEND_AFTER = 30; // seconds before "Send again" is offered

export default function LoginScreen() {
  const s = useStyles();
  const auth = useAuth();
  const [mode, setMode] = useState<Mode>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const sendCode = async () => {
    auth.clearError();
    if (await auth.requestCode(email)) { setCode(''); setMode('code'); setWait(RESEND_AFTER); }
  };
  const signInWithCode = () => { auth.clearError(); auth.loginWithCode(email, code); };
  const signInWithPassword = () => { auth.clearError(); auth.login(user, password); };
  const switchMode = (m: Mode) => { auth.clearError(); setMode(m); };

  // Desktop web gets the form in a centred card; phones and native keep the full-width layout as is.
  const { width } = useWindowDimensions();
  const desktop = Platform.OS === 'web' && width >= 700;

  const form = (
    <>
      <View style={s.logo}><Text style={s.logoText}>H</Text></View>
      <Text style={[s.title, { marginTop: 16 }]}>helm</Text>
      <Text style={s.sub}>Sign in to chat with your team and agents</Text>

      {mode === 'email' && (
        <>
          <Field label="Email" value={email} onChangeText={setEmail} autoCapitalize="none" autoCorrect={false}
            keyboardType="email-address" placeholder="you@company.com" textContentType="emailAddress" autoComplete="email"
            onSubmitEditing={sendCode} />
          {auth.error ? <Text style={s.err}>{auth.error}</Text> : null}
          <Button label="Email me a code" onPress={sendCode} busy={auth.busy} disabled={!email.trim()} />
          <Text style={s.hint}>We send a six-digit code to that address. No password needed.</Text>
        </>
      )}

      {mode === 'code' && (
        <>
          <Text style={s.sent}>Code sent to <Text style={s.sentAddr}>{email.trim()}</Text>. It works for 10 minutes.</Text>
          <Field label="Code" value={code} onChangeText={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))} keyboardType="number-pad"
            placeholder="123456" textContentType="oneTimeCode" autoComplete="one-time-code" maxLength={6} autoFocus onSubmitEditing={signInWithCode} />
          {auth.error ? <Text style={s.err}>{auth.error}</Text> : null}
          <Button label="Sign in" onPress={signInWithCode} busy={auth.busy} disabled={code.length !== 6} />
          <View style={s.links}>
            <Pressable onPress={sendCode} disabled={wait > 0 || auth.busy} hitSlop={8}>
              <Text style={[s.link, wait > 0 && s.linkDim]}>{wait > 0 ? `Send again in ${wait}s` : 'Send a new code'}</Text>
            </Pressable>
            <Pressable onPress={() => switchMode('email')} hitSlop={8}><Text style={s.link}>Use a different email</Text></Pressable>
          </View>
        </>
      )}

      {mode === 'password' && (
        <>
          <Field label="Username" value={user} onChangeText={setUser}
            autoCapitalize="none" autoCorrect={false} placeholder="yourname" textContentType="username" />
          <Field label="Password" value={password} onChangeText={setPassword}
            secureTextEntry placeholder="••••••••" textContentType="password" onSubmitEditing={signInWithPassword} />
          {auth.error ? <Text style={s.err}>{auth.error}</Text> : null}
          <Button label="Sign in" onPress={signInWithPassword} busy={auth.busy} />
        </>
      )}

      <Pressable onPress={() => switchMode(mode === 'password' ? 'email' : 'password')} hitSlop={8} style={{ marginTop: 22 }}>
        <Text style={s.link}>{mode === 'password' ? 'Sign in with an emailed code instead' : 'Use a password instead'}</Text>
      </Pressable>
      <Text style={s.server}>{HS_LABEL}</Text>
    </>
  );

  return (
    <KeyboardShift style={s.root} iosBehavior="padding">
      <ScrollView contentContainerStyle={s.scroll} keyboardShouldPersistTaps="handled">
        {desktop ? <View style={s.card}>{form}</View> : form}
      </ScrollView>
    </KeyboardShift>
  );
}

function Field(props: React.ComponentProps<typeof TextInput> & { label: string }) {
  const theme = useTheme(); const s = useStyles();
  const { label, ...rest } = props;
  return (
    <View style={{ width: '100%', marginTop: 14 }}>
      <Text style={s.fieldLabel}>{label}</Text>
      <TextInput {...rest} placeholderTextColor={theme.faint} style={s.input} />
    </View>
  );
}

function Button({ label, onPress, busy, disabled }: { label: string; onPress: () => void; busy?: boolean; disabled?: boolean }) {
  const theme = useTheme(); const s = useStyles();
  const off = !!busy || !!disabled;
  return (
    <Pressable onPress={onPress} disabled={off}
      style={({ pressed }) => [s.btn, (pressed || off) && { opacity: disabled && !busy ? 0.45 : 0.7 }]}>
      {busy ? <ActivityIndicator color={theme.accentInk} /> : <Text style={s.btnText}>{label}</Text>}
    </Pressable>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  root: { flex: 1, backgroundColor: theme.bg },
  scroll: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 28 },
  card: { width: '100%', maxWidth: 420, alignItems: 'center', backgroundColor: theme.surface2, borderWidth: 1, borderColor: theme.border, borderRadius: radius.xl, paddingVertical: 36, paddingHorizontal: 32 },
  logo: { width: 60, height: 60, borderRadius: radius.lg, backgroundColor: theme.accent, alignItems: 'center', justifyContent: 'center' },
  logoText: { color: theme.accentInk, fontWeight: '800', fontSize: 26 },
  title: { color: theme.ink, fontSize: 26, fontWeight: '700', fontFamily: theme.mono, letterSpacing: 0.5 },
  sub: { color: theme.muted, fontSize: 15, marginTop: 6, textAlign: 'center', marginBottom: 6 },
  fieldLabel: { color: theme.muted, fontSize: 12, fontWeight: '700', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, borderRadius: radius.md, color: theme.ink, fontSize: 16, paddingHorizontal: 14, paddingVertical: 12 },
  btn: { backgroundColor: theme.accent, borderRadius: radius.md, paddingVertical: 14, alignItems: 'center', width: '100%', marginTop: 18 },
  btnText: { color: theme.accentInk, fontWeight: '800', fontSize: 16 },
  err: { color: theme.danger, fontSize: 14, marginTop: 12, textAlign: 'center' },
  hint: { color: theme.faint, fontSize: 13, marginTop: 12, textAlign: 'center', lineHeight: 18 },
  sent: { color: theme.muted, fontSize: 14, marginTop: 18, textAlign: 'center', lineHeight: 20 },
  sentAddr: { color: theme.ink, fontWeight: '600' },
  links: { flexDirection: 'row', justifyContent: 'space-between', width: '100%', marginTop: 14 },
  link: { color: theme.accent, fontSize: 14, fontWeight: '600' },
  linkDim: { color: theme.faint, fontWeight: '400' },
  server: { color: theme.faint, fontSize: 12, marginTop: 22, fontFamily: theme.mono },
}));
